import type React from 'react';
import { useEffect } from 'react';

import { AdornedInput, FormField, FormHelp, Label, Paragraph } from '@zillow/constellation-10';

import type { Section } from '../../../../models/index';
import { UTILITIES_PAYMENTS } from '../../../../constants';
import { stopPropagationIfInvalidNumber } from '../../../../utils/validatorTypes/PositiveIntegerValidator';

type Props = {
  section: Section;
  setSectionValues: (keyValuePairs: object) => void;
};

export default function UtilitiesPayments({ section, setSectionValues }: Props): JSX.Element {
  const isValid = (value: string): boolean => Number(value) >= UTILITIES_PAYMENTS.BOUNDS.MIN_DAYS;
  const amountHasError = section.getField(UTILITIES_PAYMENTS.FIELD_NAME.DAYS_AFTER_BILLING).hasError;

  useEffect(() => {
    section.setCustomFieldValidator(UTILITIES_PAYMENTS.FIELD_NAME.DAYS_AFTER_BILLING, isValid);
  }, []);

  function setDaysAfterBilling(event: React.ChangeEvent<HTMLInputElement>): void {
    setSectionValues({
      [UTILITIES_PAYMENTS.FIELD_NAME.DAYS_AFTER_BILLING]: event.currentTarget.value,
    });
  }

  return (
    <>
      <Paragraph css={{ marginBottom: 'layout.tighter' }} textStyle="body-lg-bold">
        Utilities and services payment timing
      </Paragraph>
      <FormField
        required
        error={amountHasError}
        label={
          <Label id="variable-utility-costs">
            All variable utility costs will be billed monthly, and must be paid by tenant within
          </Label>
        }
        control={
          <AdornedInput.Root error={amountHasError}>
            <AdornedInput.Input
              aria-labelledby="variable-utility-costs"
              data-test="utilities-and-services-payment"
              type="number"
              onKeyDown={stopPropagationIfInvalidNumber}
              value={section.getFieldValue(UTILITIES_PAYMENTS.FIELD_NAME.DAYS_AFTER_BILLING)}
              onChange={setDaysAfterBilling}
            />
            <AdornedInput.Adornment>days after billing</AdornedInput.Adornment>
          </AdornedInput.Root>
        }
        description={
          <>
            <FormHelp>{`15 days is most common`}</FormHelp>
            {amountHasError && (
              <FormHelp>{`Enter a number higher than ${UTILITIES_PAYMENTS.BOUNDS.MIN_DAYS} days`}</FormHelp>
            )}
          </>
        }
      />
    </>
  );
}
